import { useCallback, useEffect, useState } from 'react';
import {
  useContentDispatch,
  useContentSelector,
  ContentEntityTypeActions,
  ComponentsActions,
  CollectionComponetTypesActions,
  getContentEntityListsLookups,
} from '@wajeez-web/stores/content';
import { Server } from '@wajeez/wajeez.microservices';
import { Spin } from '@wajeez-web/components/ui';

import { InputFieldWithSelectorOptions } from './ComponentFormBuilder';
import { CollectionComponentEditForm } from './CollectionComponentEditForm';
import { CollectionComponentCreateForm } from './CollectionComponentCreateForm';

type SelectorOption = {
  value: string | undefined;
  label: string | undefined;
};

type SelectorOptionsGrouped = {
  label: string | undefined;
  options: SelectorOption[];
};

export interface InputField {
  name: string | undefined;
  order: number | undefined;
  label: string | undefined;
  placeHolder: string | undefined;
  required: boolean | undefined;
  fieldType: Server.FieldType | undefined;
  fieldOptions: Server.ComponentTypeFieldOptionsOutput | undefined;
  permission: string | undefined;
}

interface CollectionComponentFormProps {
  type: 'Create' | 'Edit';
  onSubmit: () => void;
  collectionComponentTypeId?: number | undefined;
  onCancel?: () => void;
}

export const CollectionComponentForm = ({
  type,
  onSubmit,
  collectionComponentTypeId,
  onCancel,
}: CollectionComponentFormProps) => {
  const contentDispatch = useContentDispatch();

  const [inputFields, setInputFields] = useState<InputField[] | undefined>();
  const [inputFieldsWithSelectorOptions, setInputFieldsWithSelectorOptions] =
    useState<InputFieldWithSelectorOptions[] | undefined>([]);
  const [
    isGetComponentLookupsByComponentTypesIdsSuccess,
    setIsGetComponentLookupsByComponentTypesIdsSuccess,
  ] = useState(false);

  const collectionComponentType = useContentSelector(
    ({ collectionComponentTypes }) =>
      collectionComponentTypes.collectionComponentTypeById
  );
  const collectionComponentTypeData = collectionComponentType.data;

  const componentLookupsByComponentTypeIds = useContentSelector(
    ({ components }) => components.componentLookupsByComponentTypeId
  );

  const contentEntityTypesLookups = useContentSelector(
    ({ contentEntityType }) => contentEntityType.lookups
  );
  const selectSortFactors = useContentSelector(
    ({ contentEntityType }) => contentEntityType.selectSortFactors
  );

  const collectionComponentDetailsStatus = useContentSelector(
    ({ collectionComponents }) => collectionComponents.detailsStatus
  );

  const getComponentLookupsByComponentTypesIds = useCallback(async () => {
    if (inputFields) {
      const status = [];

      for (let inputField of inputFields) {
        if (inputField.fieldType === Server.FieldType.RequiredComponent) {
          const typeId = inputField.fieldOptions?.typeId;

          if (typeId && !componentLookupsByComponentTypeIds.typeIds[typeId]) {
            const res = await contentDispatch(
              ComponentsActions.getComponentLookupsByComponentTypeIdsAsync({
                componentTypeIds: [typeId],
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              status.push('fulfilled');
            } else {
              status.push('rejected');
            }
          }
        }
      }

      const isAllFulfilled = !status.find((s) => s === 'rejected');

      if (isAllFulfilled || (status.length === 0 && inputFields.length === 0)) {
        setIsGetComponentLookupsByComponentTypesIdsSuccess(true);
      }
    }
  }, [inputFields?.length, contentDispatch]);

  useEffect(() => {
    collectionComponentTypeData &&
      setInputFields(
        collectionComponentTypeData?.fields?.map((f) => {
          return {
            name: f.name,
            order: f.order,
            label: f.label,
            placeHolder: f.placeHolder,
            required: f.required,
            fieldType: f.fieldType,
            fieldOptions: f.fieldOptions,
            permission: f.permission,
          };
        })
      );

    return () => {
      setInputFields(undefined);
    };
  }, [collectionComponentTypeData?.fields]);

  useEffect(() => {
    getComponentLookupsByComponentTypesIds();
  }, [getComponentLookupsByComponentTypesIds]);

  useEffect(() => {
    if (
      inputFields &&
      contentEntityTypesLookups.length &&
      isGetComponentLookupsByComponentTypesIdsSuccess
    ) {
      const newInputFields = inputFields.map((i) => {
        return {
          ...i,
          mappedFieldOptionsGrouped: [] as SelectorOptionsGrouped[],
          componentTypesOptions: [] as SelectorOption[],
          allowedPredefinedComponentsOptions: [] as SelectorOption[],
        };
      });

      for (let newInputField of newInputFields) {
        if (newInputField.fieldType === Server.FieldType.MappedField) {
          newInputField.mappedFieldOptionsGrouped =
            contentEntityTypesLookups.map((t) => {
              return {
                label: t.displayNamePlural + ` (${t.name})`,
                options:
                  t.fields?.map((f) => {
                    return {
                      value: f.id?.toString(),
                      label: f.label,
                    };
                  }) || [],
              };
            });
        } else if (
          newInputField.fieldType === Server.FieldType.RequiredComponent
        ) {
          newInputField.componentTypesOptions =
            componentLookupsByComponentTypeIds.typeIds[
              JSON.stringify([newInputField.fieldOptions?.typeId])
            ]?.data.map((d) => {
              return {
                value: d.id,
                label: d.referenceName,
              };
            });
        } else if (
          newInputField.fieldType === Server.FieldType.PredefinedComponent
        ) {
          newInputField.allowedPredefinedComponentsOptions =
            newInputField.fieldOptions?.allowedPredefinedComponents?.map(
              (p) => {
                return {
                  value: p,
                  label: p,
                };
              }
            ) || [];
        }
      }

      setInputFieldsWithSelectorOptions(newInputFields);
    }
  }, [
    contentEntityTypesLookups.length,
    inputFields?.length,
    componentLookupsByComponentTypeIds,
    isGetComponentLookupsByComponentTypesIdsSuccess,
    contentDispatch,
  ]);

  useEffect(() => {
    collectionComponentTypeId &&
      contentDispatch(
        CollectionComponetTypesActions.getCollectionComponentTypeByIdAsync(
          collectionComponentTypeId
        )
      );

    return () => {
      contentDispatch(
        CollectionComponetTypesActions.clearCollectionComponentTypeById()
      );
    };
  }, [collectionComponentTypeId, contentDispatch]);

  useEffect(() => {
    contentDispatch(
      ContentEntityTypeActions.getContentEntityTypeLookupsAsync()
    );

    contentDispatch(ContentEntityTypeActions.getSelectSortFactorsAsync());

    getContentEntityListsLookups({});
  }, [contentDispatch]);

  return (
    <div className="w-[800px] mx-auto px-4 py-6">
      {collectionComponentType.status === 'loading' ||
      !collectionComponentTypeData ||
      componentLookupsByComponentTypeIds.status === 'loading' ||
      collectionComponentDetailsStatus === 'loading' ||
      selectSortFactors.status === 'loading' ||
      !selectSortFactors.data ||
      !isGetComponentLookupsByComponentTypesIdsSuccess ? (
        <Spin />
      ) : (
        <div className="w-full">
          <div className="w-full pb-6 border-b-[2px]">
            <h1 className="text-[26px] font-bold mb-1">
              {collectionComponentTypeData.title}
              &nbsp;
              <span className="text-[14px] font-normal">
                ({collectionComponentTypeData.name})
              </span>
            </h1>
            <h2 className="text-[17px] font-normal">
              {collectionComponentTypeData.description}
            </h2>
          </div>
          <div className="mt-6">
            {type === 'Create' ? (
              <CollectionComponentCreateForm
                inputFieldsWithSelectorOptions={inputFieldsWithSelectorOptions}
                onSubmit={onSubmit}
                onCancel={onCancel}
                collectionComponentTypeId={collectionComponentTypeData.id}
              />
            ) : (
              <CollectionComponentEditForm
                inputFieldsWithSelectorOptions={inputFieldsWithSelectorOptions}
                onSubmit={onSubmit}
                onCancel={onCancel}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
